import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatCard, DateRangeSelector, TrendIndicator } from '../components/StatCard';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { NavBar } from '../components/SubscriptionAppNavBar';
import { DateRangeTabs, DateRangeOption } from '../components/DateRangeTabs';
import { Users, DollarSign, TrendingDown, ShoppingCart, AlertTriangle, Calendar, MessageSquare, RefreshCw, CreditCard, Mail, Search, Sparkles, ArrowRight, CheckCircle, Clock, TrendingUp, Info, Smartphone, ClipboardList, Plus, Link as LinkIcon } from 'lucide-react';
import { useActivities, customerNames } from '../contexts/ActivityContext';
import { useTasks } from '../contexts/TaskContext';
import { CreateTaskModal } from '../components/CreateTaskModal';
// Mock Data
const atRiskMembers = [{
  id: 1,
  name: 'James Wilson',
  issue: 'Skipped 2 shipments',
  inactiveDays: 45,
  riskFactors: 2 // Multiple factors
}, {
  id: 2,
  name: 'Emma Thompson',
  issue: 'Payment failed',
  inactiveDays: 12,
  riskFactors: 1
}, {
  id: 3,
  name: 'Michael Chen',
  issue: 'No engagement',
  inactiveDays: 60,
  riskFactors: 1
}];
const paymentIssues = [{
  id: 1,
  name: 'James Anderson',
  subscriptionId: 'SUB-23138',
  issue: 'Card Expiring',
  days: 8,
  retriesSent: 2,
  retriesTotal: 3
}, {
  id: 2,
  name: 'Ethan White',
  subscriptionId: 'SUB-23130',
  issue: 'Failed Payment',
  days: 3,
  retriesSent: 3,
  retriesTotal: 3
}, {
  id: 3,
  name: 'Victoria Moore',
  subscriptionId: 'SUB-23121',
  issue: 'Insufficient Funds',
  days: 5,
  retriesSent: 1,
  retriesTotal: 3
}, {
  id: 4,
  name: 'Daniel Scott',
  subscriptionId: 'SUB-23120',
  issue: 'Card Declined',
  days: 7,
  retriesSent: 2,
  retriesTotal: 3
}];
const upcomingRenewals = [{
  period: 'Next 7 days',
  count: 23,
  needsAttention: 2,
  filterValue: 'next-7-days'
}, {
  period: 'Next 30 days',
  count: 67,
  needsAttention: 5,
  filterValue: 'next-30-days'
}, {
  period: 'Next 3 months',
  count: 156,
  needsAttention: 8,
  filterValue: 'next-3-months'
}];
const teamNotes = [{
  id: 1,
  author: 'Sarah',
  member: 'James Wilson',
  note: 'Prefers reds only, no Chardonnay',
  time: '2h ago'
}, {
  id: 2,
  author: 'Mike',
  member: 'Emma Thompson',
  note: 'Called about failed payment, updating card tomorrow',
  time: '4h ago'
}, {
  id: 3,
  author: 'System',
  member: 'Robert Fox',
  note: 'Automated dunning email 2 sent',
  time: '5h ago'
}, {
  id: 4,
  author: 'Sarah',
  member: 'Alice Johnson',
  note: 'Upgraded to 12-bottle case for holiday season',
  time: '1d ago'
}];
const boxActivity = [{
  id: 1,
  member: 'David Miller',
  action: 'Swapped 2 bottles',
  time: '10m ago'
}, {
  id: 2,
  member: 'Lisa Anderson',
  action: 'Added 3 bottles',
  time: '45m ago'
}, {
  id: 3,
  member: 'Tom Wilson',
  action: 'Built new box',
  time: '1h ago'
}];
// Mock task data for My Tasks widget
const myTasks = [{
  id: '1',
  title: "Chase Emma's failed payment",
  priority: 'Urgent' as const,
  dueDate: 'Overdue',
  isOverdue: true,
  linkedTo: {
    id: 'SUB-23138',
    type: 'subscription'
  }
}, {
  id: '2',
  title: "Follow up on David's tier upgrade",
  priority: 'Medium' as const,
  dueDate: 'Due today',
  isOverdue: false,
  linkedTo: {
    id: 'MEM-1087',
    type: 'member'
  }
}, {
  id: '3',
  title: 'Call Sarah about address change',
  priority: 'High' as const,
  dueDate: 'Due tomorrow',
  isOverdue: false,
  linkedTo: {
    id: 'Sarah Johnson',
    type: 'contact'
  }
}, {
  id: '4',
  title: 'Review March wine allocation',
  priority: 'Medium' as const,
  dueDate: 'In 3 days',
  isOverdue: false,
  linkedTo: null
}, {
  id: '5',
  title: 'Prepare April club newsletter',
  priority: 'Low' as const,
  dueDate: 'Next week',
  isOverdue: false,
  linkedTo: null
}];
function getPriorityDotColor(priority: string) {
  switch (priority) {
    case 'Urgent':
      return 'bg-[#EF4444]';
    case 'High':
      return 'bg-[#F97316]';
    case 'Medium':
      return 'bg-[#F59E0B]';
    case 'Low':
      return 'bg-[#10B981]';
    default:
      return 'bg-[#94A3B8]';
  }
}
// Contextual Trend Indicator Component
interface ContextualTrendProps {
  value: number;
  label: string;
  direction: 'up' | 'down';
  isInverse?: boolean; // For metrics where increase is bad (e.g., churn rate)
}
function ContextualTrend({
  value,
  label,
  direction,
  isInverse = false
}: ContextualTrendProps) {
  // Determine if the change is positive for the business
  const isPositiveChange = isInverse ? direction === 'down' : direction === 'up';
  const color = isPositiveChange ? '#10B981' : '#EF4444';
  const Icon = direction === 'up' ? TrendingUp : TrendingDown;
  return <div className="flex items-center gap-1.5" data-id="element-9713">
      <Icon className="w-3 h-3" style={{
      color
    }} data-id="element-9714" />
      <span className="text-xs font-medium" style={{
      color
    }} data-id="element-9715">
        {direction === 'up' ? '+' : '-'}
        {Math.abs(value)}%
      </span>
      <span className="text-xs text-[#94A3B8]" data-id="element-9716">{label}</span>
    </div>;
}
// Retry Progress Dots Component
interface RetryProgressProps {
  sent: number;
  total: number;
}
function RetryProgress({
  sent,
  total
}: RetryProgressProps) {
  return <div className="flex items-center gap-1" title={`${sent}/${total} retries sent`} data-id="element-9717">
      {Array.from({
      length: total
    }).map((_, index) => <div key={index} className={`w-1.5 h-1.5 rounded-full ${index < sent ? 'bg-[#0EA5E9]' : 'bg-[#E2E8F0]'}`} data-id="element-9718" />)}
    </div>;
}
// Helper function to get urgency styling
function getUrgencyStyle(days: number) {
  if (days < 1) {
    return {
      bg: 'bg-[#FEF2F2]',
      border: 'border-l-2 border-[#EF4444]',
      textColor: 'text-[#EF4444]'
    };
  } else if (days <= 3) {
    return {
      bg: 'bg-[#FFFBEB]',
      border: 'border-l-2 border-[#F59E0B]',
      textColor: 'text-[#F59E0B]'
    };
  } else {
    return {
      bg: '',
      border: '',
      textColor: 'text-[#EF4444]'
    };
  }
}
// Helper function to get issue color
function getIssueColor(issue: string) {
  if (issue.includes('Expiring')) return 'text-[#F59E0B]';
  return 'text-[#EF4444]';
}
// Risk Severity Component
interface RiskSeverityProps {
  inactiveDays: number;
  riskFactors: number;
}
function getRiskLevel(inactiveDays: number, riskFactors: number): 'high' | 'medium' | 'low' {
  if (inactiveDays >= 45 || riskFactors >= 2) return 'high';
  if (inactiveDays >= 14 || riskFactors === 1) return 'medium';
  return 'low';
}
function RiskDot({
  inactiveDays,
  riskFactors
}: RiskSeverityProps) {
  const level = getRiskLevel(inactiveDays, riskFactors);
  const colors = {
    high: 'bg-[#EF4444]',
    medium: 'bg-[#F59E0B]',
    low: 'bg-[#FCD34D]'
  };
  return <div className={`w-2 h-2 rounded-full ${colors[level]} flex-shrink-0`} data-id="element-9719" />;
}
// Tooltip Component
function InfoTooltip({
  text
}: {
  text: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  return <div className="relative inline-block" data-id="element-9720">
      <button onMouseEnter={() => setShowTooltip(true)} onMouseLeave={() => setShowTooltip(false)} className="text-[#94A3B8] hover:text-[#64748B] transition-colors" data-id="element-9721">
        <Info className="w-4 h-4" data-id="element-9722" />
      </button>
      {showTooltip && <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-[#0F172A] text-white text-xs rounded-lg p-3 shadow-lg z-10" data-id="element-9723">
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#0F172A]" data-id="element-9724" />
          {text}
        </div>}
    </div>;
}
export function WineClubDashboard() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('This Month');
  // Get all notes from ActivityContext
  const {
    getAllNotes
  } = useActivities();
  const allNotes = getAllNotes();
  // Get tasks from TaskContext
  const {
    getTasksByUser,
    addTask,
    updateTask
  } = useTasks();
  const allMyTasks = getTasksByUser('Sarah Chen');
  const openTasks = allMyTasks.filter(t => t.status !== 'Complete' && t.status !== 'Cancelled');
  const completedTasks = allMyTasks.filter(t => t.status === 'Complete');
  // Track which completed tasks the user has cleared from the widget
  const [clearedTaskIds, setClearedTaskIds] = useState<Set<string>>(new Set());
  // Show open tasks first, then completed (excluding cleared ones), max 8 total
  const visibleCompleted = completedTasks.filter(t => !clearedTaskIds.has(t.id));
  const displayTasks = [...openTasks, ...visibleCompleted].slice(0, 8);
  const handleClearTask = (taskId: string) => {
    setClearedTaskIds(prev => new Set([...prev, taskId]));
  };
  const handleClearAllCompleted = () => {
    const allCompletedIds = completedTasks.map(t => t.id);
    setClearedTaskIds(prev => new Set([...prev, ...allCompletedIds]));
  };
  // Create Task modal state
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  // Responsive state - only check for mobile
  const [isMobile, setIsMobile] = useState(false);
  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768); // Less than tablet (md breakpoint)
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  // Date Range State
  const [selectedRange, setSelectedRange] = useState<DateRangeOption>('30days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  // Helper for comparison labels
  const getComparisonLabel = (range: DateRangeOption) => {
    switch (range) {
      case 'today':
        return 'vs yesterday';
      case '7days':
        return 'vs prev 7 days';
      case '30days':
        return 'vs prev 30 days';
      case '90days':
        return 'vs prev 90 days';
      case 'year':
        return 'vs last year';
      case 'custom':
        return 'vs prior period';
      default:
        return 'vs last month';
    }
  };
  const handleCustomDateChange = (start: string, end: string) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
  };
  // Sort payment issues by urgency (fewest days first)
  const sortedPaymentIssues = [...paymentIssues].sort((a, b) => a.days - b.days);
  // Sort at-risk members by risk severity
  const sortedAtRiskMembers = [...atRiskMembers].sort((a, b) => {
    const levelOrder = {
      high: 0,
      medium: 1,
      low: 2
    };
    const aLevel = getRiskLevel(a.inactiveDays, a.riskFactors);
    const bLevel = getRiskLevel(b.inactiveDays, b.riskFactors);
    if (levelOrder[aLevel] !== levelOrder[bLevel]) {
      return levelOrder[aLevel] - levelOrder[bLevel];
    }
    // If same level, sort by inactive days (most inactive first)
    return b.inactiveDays - a.inactiveDays;
  });
  // Mobile incompatibility screen
  if (isMobile) {
    return <div className="bg-[#F1F5F9] min-h-screen flex items-center justify-center p-6" data-id="element-9725">
        <div className="max-w-md text-center" data-id="element-9726">
          <div className="mb-6 flex justify-center" data-id="element-9727">
            <div className="w-20 h-20 rounded-full bg-[#E0F2FE] flex items-center justify-center" data-id="element-9728">
              <Smartphone className="w-10 h-10 text-[#0EA5E9]" data-id="element-9729" />
            </div>
          </div>
          <h1 className="text-[24px] font-semibold text-[#0F172A] mb-3" data-id="element-9730">
            Mobile Version Coming Soon
          </h1>
          <p className="text-[14px] text-[#64748B] mb-6 leading-relaxed" data-id="element-9731">
            The Wine Club Dashboard is optimized for desktop and tablet devices.
            We're working on a mobile-friendly version that will be available
            soon.
          </p>
          <div className="bg-white rounded-lg border border-[#E2E8F0] p-4" data-id="element-9732">
            <p className="text-[13px] text-[#475569] font-medium mb-2" data-id="element-9733">
              For the best experience:
            </p>
            <ul className="text-[13px] text-[#64748B] text-left space-y-1" data-id="element-9734">
              <li data-id="element-9735">• Use a tablet (768px or wider)</li>
              <li data-id="element-9736">• Use a desktop computer</li>
              <li data-id="element-9737">• Rotate your device to landscape</li>
            </ul>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-9738">
      {/* NavBar */}
      <div className="mb-6" data-id="element-9739">
        <NavBar tabs={[]} title="Wine Club Dashboard" data-id="element-9740" />
      </div>

      {/* Date Range Tabs */}
      <div className="mb-6" data-id="element-9741">
        <DateRangeTabs value={selectedRange} onChange={setSelectedRange} customStartDate={customStartDate} customEndDate={customEndDate} onCustomDateChange={handleCustomDateChange} data-id="element-9742" />
      </div>

      {/* Top Row: Key Metrics with Contextual Colors */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-9743">
        {/* Active Members - Green when up, Red when down */}
        <StatCard label="Active Members" value={847} trend={<ContextualTrend value={12} label={getComparisonLabel(selectedRange)} direction="up" isInverse={false} data-id="element-9745" />} data-id="element-9744" />

        {/* Monthly Revenue - Green when up, Red when down */}
        <StatCard label="Monthly Revenue" value="$42,350" trend={<ContextualTrend value={8} label={getComparisonLabel(selectedRange)} direction="up" isInverse={false} data-id="element-9747" />} data-id="element-9746" />

        {/* Churn Rate - INVERSE: Red when up (bad), Green when down (good) */}
        <StatCard label="Churn Rate" value="2.1%" trend={<ContextualTrend value={0.5} label={getComparisonLabel(selectedRange)} direction="up" isInverse={true} data-id="element-9749" />} data-id="element-9748" />

        {/* Avg Order Value - Green when up, Red when down */}
        <StatCard label="Avg Order Value" value="$186" trend={<ContextualTrend value={12} label={getComparisonLabel(selectedRange)} direction="up" isInverse={false} data-id="element-9751" />} data-id="element-9750" />
      </div>

      <hr className="my-10 border-[#CBD5E1]" data-id="element-9752" />

      {/* Row 2: At Risk & Renewals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-9753">
        {/* At Risk Members - Payment Issues */}
        <Card padding="none" className="flex flex-col" data-id="element-9754">
          <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-9755">
            <div className="flex items-center gap-2" data-id="element-9756">
              <AlertTriangle className="w-5 h-5 text-[#EF4444]" data-id="element-9757" />
              <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-9758">
                Payment Issues
              </h3>
            </div>
            <Badge variant="error" data-id="element-9759">8 Critical</Badge>
          </div>
          <div className="p-6 bg-white rounded-b-lg flex-1" data-id="element-9760">
            <div className="space-y-3" data-id="element-9761">
              {sortedPaymentIssues.map(member => {
              const urgencyStyle = getUrgencyStyle(member.days);
              const issueColor = getIssueColor(member.issue);
              return <div key={member.id} className={`flex justify-between items-start p-3 rounded-md transition-colors ${urgencyStyle.bg} ${urgencyStyle.border}`} data-id="element-9762">
                    <div className="flex-1" data-id="element-9763">
                      <button onClick={() => navigate(`/subscriptions/${member.subscriptionId}`)} className="font-medium text-sm text-[#0EA5E9] hover:text-[#0284C7] transition-colors text-left" data-id="element-9764">
                        {member.name}
                      </button>
                      <div className={`text-[13px] ${issueColor} flex items-center gap-1 mt-1`} data-id="element-9765">
                        <CreditCard className="w-3 h-3" data-id="element-9766" /> {member.issue}
                      </div>
                    </div>
                    <div className="text-right ml-4" data-id="element-9767">
                      <div className={`text-[13px] font-semibold ${urgencyStyle.textColor}`} data-id="element-9768">
                        {member.days} {member.days === 1 ? 'day' : 'days'} left
                      </div>
                      <div className="mt-1.5 flex justify-end" data-id="element-9769">
                        <RetryProgress sent={member.retriesSent} total={member.retriesTotal} data-id="element-9770" />
                      </div>
                    </div>
                  </div>;
            })}
            </div>
            <button onClick={() => navigate('/subscriptions', {
            state: {
              filterValues: {
                status: ['at-risk']
              }
            }
          })} className="w-full mt-4 text-[13px] text-[#0EA5E9] font-medium hover:text-[#0284C7] flex items-center justify-center gap-1 transition-colors" data-id="element-9771">
              View all payment issues <ArrowRight className="w-4 h-4" data-id="element-9772" />
            </button>
          </div>
        </Card>

        {/* At Risk Members - Engagement */}
        <Card padding="none" className="flex flex-col" data-id="element-9773">
          <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-9774">
            <div className="flex items-center gap-2" data-id="element-9775">
              <Users className="w-5 h-5 text-[#F59E0B]" data-id="element-9776" />
              <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-9777">
                Engagement Risk
              </h3>
              <InfoTooltip text="Members flagged due to: extended inactivity, skipped shipments, failed payments, or no recent engagement." data-id="element-9778" />
            </div>
            <Badge variant="warning" data-id="element-9779">3 Members</Badge>
          </div>
          <div className="p-6 bg-white rounded-b-lg flex-1" data-id="element-9780">
            <div className="space-y-3" data-id="element-9781">
              {sortedAtRiskMembers.map(member => <div key={member.id} className="flex justify-between items-start gap-3" data-id="element-9782">
                  <div className="flex items-start gap-2 flex-1" data-id="element-9783">
                    <div className="mt-1.5" data-id="element-9784">
                      <RiskDot inactiveDays={member.inactiveDays} riskFactors={member.riskFactors} data-id="element-9785" />
                    </div>
                    <div className="flex-1" data-id="element-9786">
                      <button onClick={() => navigate(`/subscriptions/${member.id}`)} className="font-medium text-sm text-[#0EA5E9] hover:text-[#0284C7] transition-colors text-left" data-id="element-9787">
                        {member.name}
                      </button>
                      <div className="text-[13px] text-[#64748B] mt-1" data-id="element-9788">
                        {member.issue}
                      </div>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-[#EF4444] bg-[#FEF2F2] px-2 py-1 rounded whitespace-nowrap" data-id="element-9789">
                    {member.inactiveDays}d inactive
                  </span>
                </div>)}
            </div>
            <div className="mt-4 flex justify-end" data-id="element-9790">
              <button onClick={() => navigate('/subscriptions', {
              state: {
                filterValues: {
                  status: ['at-risk']
                }
              }
            })} className="text-[13px] text-[#0EA5E9] font-medium hover:text-[#0284C7] flex items-center gap-1 transition-colors" data-id="element-9791">
                View all at-risk members <ArrowRight className="w-4 h-4" data-id="element-9792" />
              </button>
            </div>
          </div>
        </Card>

        {/* Upcoming Renewals */}
        <Card padding="none" className="flex flex-col" data-id="element-9793">
          <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-9794">
            <div className="flex items-center gap-2" data-id="element-9795">
              <Calendar className="w-5 h-5 text-[#0EA5E9]" data-id="element-9796" />
              <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-9797">
                Upcoming Renewals
              </h3>
            </div>
          </div>
          <div className="p-6 bg-white rounded-b-lg flex-1 flex flex-col justify-between" data-id="element-9798">
            <div className="space-y-2" data-id="element-9799">
              {upcomingRenewals.map((item, idx) => <div key={idx} onClick={() => navigate('/subscriptions', {
              state: {
                filterValues: {
                  nextOrderDate: [item.filterValue]
                }
              }
            })} className="flex justify-between items-start p-3 bg-[#F8FAFC] rounded-lg hover:bg-[#F1F5F9] transition-colors cursor-pointer group" data-id="element-9800">
                  <span className="text-sm text-[#475569] font-medium" data-id="element-9801">
                    {item.period}
                  </span>
                  <div className="text-right" data-id="element-9802">
                    <div className="text-xl font-bold text-[#0F172A] group-hover:text-[#0EA5E9] transition-colors" data-id="element-9803">
                      {item.count}
                    </div>
                    {item.needsAttention > 0 && <div className="flex items-center gap-1 mt-1 justify-end" data-id="element-9804">
                        <AlertTriangle className="w-3 h-3 text-[#F59E0B]" data-id="element-9805" />
                        <span className="text-xs text-[#F59E0B] font-medium" data-id="element-9806">
                          {item.needsAttention} need attention
                        </span>
                      </div>}
                  </div>
                </div>)}
            </div>
            <div className="mt-4 flex justify-end" data-id="element-9807"></div>
          </div>
        </Card>
      </div>

      {/* Row 3: Activity & Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-9808">
        {/* Team Notes Feed */}
        <div className="lg:col-span-2" data-id="element-9809">
          <Card padding="none" data-id="element-9810">
            <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-9811">
              <div className="flex items-center gap-2" data-id="element-9812">
                <MessageSquare className="w-5 h-5 text-[#64748B]" data-id="element-9813" />
                <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-9814">
                  Recent Team Activity
                </h3>
              </div>
              <Button variant="ghost" size="sm" className="text-[#0EA5E9]" onClick={() => navigate('/operations/activity-log', {
              state: {
                filterByNotes: true
              }
            })} data-id="element-9815">
                View All
              </Button>
            </div>
            <div className="p-0 bg-white rounded-b-lg" data-id="element-9816">
              {allNotes.slice(0, 9).map((note, idx) => {
              const customerName = customerNames[note.customerId] || 'Unknown Customer';
              const authorInitial = note.author?.charAt(0) || 'S';
              // Calculate relative time
              const noteDate = new Date(note.date);
              const now = new Date();
              const diffMs = now.getTime() - noteDate.getTime();
              const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              let timeAgo;
              if (diffHours < 1) {
                timeAgo = 'Just now';
              } else if (diffHours < 24) {
                timeAgo = `${diffHours}h ago`;
              } else if (diffDays === 1) {
                timeAgo = '1d ago';
              } else {
                timeAgo = `${diffDays}d ago`;
              }
              return <div key={note.id} className={`p-4 flex gap-4 hover:bg-[#F8FAFC] transition-colors ${idx !== allNotes.slice(0, 9).length - 1 ? 'border-b border-[#F1F5F9]' : ''}`} data-id="element-9817">
                    <div className="w-8 h-8 rounded-full bg-[#E0F2FE] text-[#0369A1] flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-9818">
                      {authorInitial}
                    </div>
                    <div className="flex-1" data-id="element-9819">
                      <div className="flex justify-between items-start" data-id="element-9820">
                        <p className="text-sm text-[#0F172A]" data-id="element-9821">
                          <span className="font-semibold" data-id="element-9822">
                            {note.author || 'Admin'}
                          </span>{' '}
                          added note on{' '}
                          <button onClick={() => navigate(`/contacts/${note.customerId}`)} className="font-semibold text-[#0EA5E9] hover:underline" data-id="element-9823">
                            {customerName}
                          </button>
                        </p>
                        <span className="text-xs text-[#94A3B8] whitespace-nowrap" data-id="element-9824">
                          {timeAgo}
                        </span>
                      </div>
                      <p className="text-sm text-[#475569] mt-1 bg-[#F8FAFC] p-2 rounded border border-[#E2E8F0] italic" data-id="element-9825">
                        "{note.description}"
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </Card>
        </div>

        {/* Right Column: My Tasks, Box Activity & Recovery */}
        <div className="space-y-6" data-id="element-9826">
          {/* My Tasks Widget */}
          <Card padding="none" className="flex flex-col" data-id="element-9827">
            <div className="p-5 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-9828">
              <div className="flex items-center gap-2.5" data-id="element-9829">
                <div className="w-6 h-6 rounded bg-[#0EA5E9] flex items-center justify-center" data-id="element-9830">
                  <CheckCircle className="w-4 h-4 text-white" data-id="element-9831" />
                </div>
                <h3 className="text-[15px] font-bold text-[#0F172A]" data-id="element-9832">
                  My Tasks
                </h3>
                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#64748B] text-xs font-medium border border-[#E2E8F0]" data-id="element-9833">
                  {openTasks.length} open
                </span>
              </div>
              <div className="flex bg-[#F1F5F9] rounded-lg p-0.5" data-id="element-9834">
                {['Mine', 'Team', 'All'].map((tab, idx) => <button key={tab} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${idx === 0 ? 'bg-[#0F172A] text-white shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-9835">
                    {tab}
                  </button>)}
              </div>
            </div>
            <div className="bg-white rounded-b-lg" data-id="element-9836">
              <div className="divide-y divide-[#F1F5F9]" data-id="element-9837">
                {displayTasks.map(task => {
                const isOverdue = new Date(task.dueDate) < new Date('2026-02-12');
                const isDueToday = task.dueDate === 'Feb 12 2026';
                const isCompleted = task.status === 'Complete';
                return <div key={task.id} className="px-5 py-3.5 flex items-start gap-3 hover:bg-[#F8FAFC] transition-colors cursor-pointer group" data-id="element-9838">
                      <div className="mt-0.5 flex-shrink-0" data-id="element-9839">
                        <button onClick={e => {
                      e.stopPropagation();
                      if (isCompleted) {
                        updateTask(task.id, {
                          status: 'Open',
                          completedAt: undefined,
                          completedBy: undefined
                        });
                      } else {
                        updateTask(task.id, {
                          status: 'Complete',
                          completedAt: new Date().toISOString(),
                          completedBy: 'Sarah Chen'
                        });
                      }
                    }} className={`w-[18px] h-[18px] rounded border-2 flex items-center justify-center transition-colors ${isCompleted ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1] hover:border-[#94A3B8]'}`} data-id="element-9840">
                          {isCompleted && <svg width="10" height="10" viewBox="0 0 10 10" fill="none" data-id="element-9841">
                              <path d="M2 5L4.5 7.5L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-9842" />
                            </svg>}
                        </button>
                      </div>
                      <div className="flex-1 min-w-0" onClick={() => navigate('/operations/tasks')} data-id="element-9843">
                        <div className={`text-sm font-medium truncate ${isCompleted ? 'text-[#94A3B8] line-through' : 'text-[#0F172A]'}`} data-id="element-9844">
                          {task.title}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap" data-id="element-9845">
                          <span className={`text-xs ${isCompleted ? 'text-[#94A3B8]' : 'text-[#64748B]'}`} data-id="element-9846">
                            {task.assignee?.name || 'Sarah Admin'}
                          </span>
                          <span className="text-[#CBD5E1]" data-id="element-9847">·</span>
                          {isCompleted ? <span className="text-xs text-[#10B981] font-medium flex items-center gap-1" data-id="element-9848">
                              ✓ Completed yesterday
                            </span> : <span className={`text-xs font-medium flex items-center gap-1 ${isOverdue ? 'text-[#EF4444]' : isDueToday ? 'text-[#F59E0B]' : 'text-[#64748B]'}`} data-id="element-9849">
                              <Clock className="w-3 h-3" data-id="element-9850" />{' '}
                              {isOverdue ? `Overdue (${Math.abs(Math.ceil((new Date(task.dueDate).getTime() - new Date('2026-02-12').getTime()) / (1000 * 60 * 60 * 24)))} days)` : isDueToday ? 'Due today' : `Due ${new Date(task.dueDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}`}
                            </span>}
                          {task.linkedTo && !isCompleted && <>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[11px] font-medium text-[#0EA5E9] border border-[#E2E8F0]" data-id="element-9851">
                                {task.linkedTo.label || task.linkedTo.id}
                              </span>
                            </>}
                        </div>
                      </div>
                      {isCompleted && <button onClick={e => {
                    e.stopPropagation();
                    handleClearTask(task.id);
                  }} className="mt-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-[#94A3B8] hover:text-[#EF4444] p-0.5 rounded hover:bg-[#FEF2F2]" title="Clear task" data-id="element-9852">
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" data-id="element-9853">
                            <path d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-9854" />
                          </svg>
                        </button>}
                    </div>;
              })}
              </div>
              <div className="px-5 py-3 border-t border-[#E2E8F0] flex items-center justify-between" data-id="element-9855">
                <button onClick={() => navigate('/operations/tasks')} className="text-[13px] text-[#0EA5E9] font-medium hover:text-[#0284C7] transition-colors flex items-center gap-1" data-id="element-9856">
                  View all tasks <ArrowRight className="w-3.5 h-3.5" data-id="element-9857" />
                </button>
                {visibleCompleted.length > 0 && <button onClick={handleClearAllCompleted} className="text-[12px] text-[#94A3B8] font-medium hover:text-[#64748B] transition-colors" data-id="element-9858">
                    Clear completed
                  </button>}
              </div>
            </div>
          </Card>

          {/* Build-a-Box Activity */}
          <Card padding="none" data-id="element-9859">
            <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-9860">
              <div className="flex items-center gap-2" data-id="element-9861">
                <div className="w-5 h-5 text-[#8B5CF6]" data-id="element-9862" />
                <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-9863">
                  Box Activity
                </h3>
              </div>
            </div>
            <div className="p-6 bg-white rounded-b-lg" data-id="element-9864">
              <div className="space-y-4" data-id="element-9865">
                {boxActivity.map(activity => <div key={activity.id} className="flex items-start gap-3" data-id="element-9866">
                    <div className="mt-1 w-2 h-2 rounded-full bg-[#8B5CF6]" data-id="element-9867"></div>
                    <div data-id="element-9868">
                      <div className="text-sm font-medium text-[#0F172A]" data-id="element-9869">
                        {activity.member}
                      </div>
                      <div className="text-xs text-[#64748B]" data-id="element-9870">
                        {activity.action} • {activity.time}
                      </div>
                    </div>
                  </div>)}
              </div>
              <button className="w-full mt-4 text-[13px] text-[#0EA5E9] font-medium hover:text-[#0284C7]" data-id="element-9871">
                View customization report
              </button>
            </div>
          </Card>

          {/* Recovery Summary */}
          <Card padding="none" data-id="element-9872">
            <div className="p-6 border-b border-[#E2E8F0] flex justify-between items-center bg-white rounded-t-lg" data-id="element-9873">
              <div className="flex items-center gap-2" data-id="element-9874">
                <RefreshCw className="w-5 h-5 text-[#10B981]" data-id="element-9875" />
                <h3 className="text-sm font-semibold text-[#0F172A]" data-id="element-9876">
                  Recovery
                </h3>
              </div>
              <Badge variant="success" data-id="element-9877">84% Rate</Badge>
            </div>
            <div className="p-6 bg-white rounded-b-lg" data-id="element-9878">
              <div className="flex justify-between items-end mb-2" data-id="element-9879">
                <span className="text-sm text-[#64748B]" data-id="element-9880">
                  Recovered Revenue
                </span>
                <span className="text-xl font-bold text-[#10B981]" data-id="element-9881">$4,250</span>
              </div>
              <div className="w-full bg-[#F1F5F9] rounded-full h-2 mb-4" data-id="element-9882">
                <div className="bg-[#10B981] h-2 rounded-full" style={{
                width: '84%'
              }} data-id="element-9883"></div>
              </div>
              <div className="text-xs text-[#64748B]" data-id="element-9884">
                18 of 21 failed payments recovered this month
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Quick Actions Strip */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white border border-[#E2E8F0] shadow-lg rounded-full px-6 py-3 flex items-center gap-4 z-50" data-id="element-9885">
        <span className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider mr-2" data-id="element-9886">
          Quick Actions
        </span>

        <button className="flex items-center gap-2 text-[13px] font-medium text-[#475569] hover:text-[#0EA5E9] transition-colors" data-id="element-9887">
          <AlertTriangle className="w-4 h-4" data-id="element-9888" />
          View At-Risk
        </button>
        <div className="w-px h-4 bg-[#E2E8F0]" data-id="element-9889"></div>

        <button className="flex items-center gap-2 text-[13px] font-medium text-[#475569] hover:text-[#0EA5E9] transition-colors" data-id="element-9890">
          <CreditCard className="w-4 h-4" data-id="element-9891" />
          Process Pending
        </button>
        <div className="w-px h-4 bg-[#E2E8F0]" data-id="element-9892"></div>

        <button className="flex items-center gap-2 text-[13px] font-medium text-[#475569] hover:text-[#0EA5E9] transition-colors" data-id="element-9893">
          <Mail className="w-4 h-4" data-id="element-9894" />
          Klaviyo Campaign
        </button>
      </div>

      {/* Create Task Modal */}
      <CreateTaskModal isOpen={isCreateTaskModalOpen} onClose={() => setIsCreateTaskModalOpen(false)} onCreate={taskData => {
      addTask({
        ...taskData,
        status: 'Open',
        createdBy: 'Sarah Chen',
        linkedTo: taskData.linkedTo ? {
          id: taskData.linkedTo.id,
          type: taskData.linkedTo.type as any,
          label: taskData.linkedTo.label
        } : null,
        assignee: {
          name: taskData.assignee,
          avatar: taskData.assignee.split(' ').map(n => n[0]).join(''),
          email: `${taskData.assignee.toLowerCase().replace(' ', '.')}@acmewine.com`
        }
      });
      setIsCreateTaskModalOpen(false);
    }} data-id="element-9895" />
    </div>;
}